import React, { useState, useEffect, useRef, startTransition, useLayoutEffect } from "react";
import { useAuth, getSignedInHint } from "../shared/auth";
import { resolveDisplayName } from "../shared/profile";
import { GAME_VERSION } from "../shared/version";
import FullscreenButton from "../shared/FullscreenButton";
import { flagUrl } from "../shared/flags";
import { useLocalRoster } from "../pages/lobby/start/hooks";
import { useUnopenedAchievements } from "../shared/achievements-open";
import { useNewUnlocks } from "../shared/shop/new-unlocks";
import { useRoyaleClaimable } from "../shared/royale";
import PlayerMenu from "../pages/lobby/start/PlayerMenu";
import { useAnnouncement } from "../shared/announcement";
import { asset } from "../shared/portal-env";

export type ShellSection = "lobby" | "local" | "news" | "about" | "leaderboard" | "shop" | "profile" | "settings" | "help" | "admin";

interface NavBarProps {
  active: ShellSection | null;
  onNavigate: (section: ShellSection) => void;
  onLogin: () => void;
  onAccount: () => void;
}

// The words on the bar: the places you go. `secondary` ones drop off the bar
// earlier (into the hamburger). `shop`: the Store link, in gold.
// `tight`: also goes to the menu at tablet widths (1080px and below).
const LINKS: { id: ShellSection; label: string; secondary?: boolean; signedInOnly?: boolean; tight?: boolean }[] = [
  { id: "lobby", label: "Play" },
  { id: "shop", label: "Store" },
  // for everyone: signed out it is the way to sign in and build one
  { id: "profile", label: "Profile" },
  { id: "leaderboard", label: "Leaderboard", tight: true },
  { id: "about", label: "About", secondary: true },
];
// The icons beside them (the hamburger's menu lists them by name). The bar is
// the same for everyone: admins reach their pages from Settings.
const svg = (d: React.ReactNode) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">{d}</svg>
);
const ICONS: { id: ShellSection; label: string; icon: React.ReactNode }[] = [
  { id: "news", label: "News", icon: svg(<><path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9" /><path d="M10.3 21a1.94 1.94 0 0 0 3.4 0" /></>) },
  { id: "help", label: "Help", icon: svg(<><circle cx="12" cy="12" r="10" /><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" /><path d="M12 17h.01" /></>) },
  { id: "settings", label: "Settings", icon: svg(<><path d="M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z" /><circle cx="12" cy="12" r="3" /></>) },
];

export default function NavBar({ active, onNavigate, onLogin, onAccount }: NavBarProps) {
  // a dot on the News bell until the latest post is read
  const { unread: newsUnread } = useAnnouncement();
  const { user, loading } = useAuth();
  const [menuOpen, setMenuOpen] = useState(false);
  // Optimistic first-frame auth render (see the auth slot below). During the
  // async session check, fall back to the last-known state so the right control
  // is on screen from the first paint instead of popping in.
  const [signedInHint] = useState(getSignedInHint);
  const showSignIn = loading ? !signedInHint : !user;
  // Same optimism for the LINKS: a returning signed-in visitor gets the
  // Profile link from the first paint, so the centered bar keeps its exact
  // width through the session check instead of shifting when it lands.
  const probablySignedIn = loading ? signedInHint : !!user;
  const navRef = useRef<HTMLElement>(null);
  // The profile chip: player 1 on this device (flag, name) over their best
  // ranked ladder. It opens the Players popover (name, colour, flag, keys, and
  // more players on this PC).
  const roster = useLocalRoster();
  const me = roster.players[0];
  const [playersOpen, setPlayersOpen] = useState(false);
  // (Measured before the first paint, a layout effect: pages sized by it, the
  // start page's two columns, would otherwise be drawn once with the guess and
  // then jump to the real height.)
  // Publish the nav's rendered height as --nav-h on the root, so the fixed
  // section-panel layer can start exactly one edge-gap below it instead of
  // guessing (the nav is shorter on phones and in landscape).
  useLayoutEffect(() => {
    const el = navRef.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    const root = document.documentElement;
    const publish = () => root.style.setProperty("--nav-h", `${Math.ceil(el.getBoundingClientRect().height)}px`);
    publish();
    const ro = new ResizeObserver(publish);
    ro.observe(el);
    return () => { ro.disconnect(); root.style.removeProperty("--nav-h"); };
  }, []);
  // Phones held upright: the bar hides as you scroll down and slides back as
  // soon as you scroll up a little (the way the browser's own toolbar does),
  // via an `app-nav-away` class on the root; the stylesheet moves the bar and
  // a section's sticky header together. Thresholds so a single jittery scroll
  // event (Android fires some when its toolbar collapses) never flips it, and
  // near the top of the page the bar is simply always there.
  useEffect(() => {
    if (typeof matchMedia !== "function") return;
    const phone = matchMedia("(max-width: 900px), (pointer: coarse)");
    const portrait = matchMedia("(orientation: portrait)");
    const root = document.documentElement;
    let lastY = window.scrollY;
    let run = 0; // signed distance scrolled in the current direction
    let away = false;
    const set = (h: boolean) => {
      if (h === away) return;
      away = h;
      root.classList.toggle("app-nav-away", h);
    };
    const onScroll = () => {
      if (!phone.matches || !portrait.matches) { set(false); return; }
      const y = window.scrollY;
      const dy = y - lastY;
      lastY = y;
      if (y < 50) { run = 0; set(false); return; }
      if ((dy > 0) !== (run > 0)) run = 0;
      run += dy;
      if (run > 10) set(true);
      else if (run < -10) set(false);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    portrait.addEventListener("change", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll);
      portrait.removeEventListener("change", onScroll);
      root.classList.remove("app-nav-away");
    };
  }, []);
  // Profile only exists for signed-in players; guests don't get a dead link.
  const unopened = useUnopenedAchievements();
  // won items not looked at yet, and Royale rewards waiting to be claimed
  const newItems = [...useNewUnlocks(), ...useRoyaleClaimable()];
  const links = LINKS.filter((l) => !l.signedInOnly || probablySignedIn);
  const icons = ICONS;

  // Close on click/tap outside the nav
  useEffect(() => {
    if (!menuOpen) return;
    function handlePointer(e: MouseEvent) {
      if (navRef.current && !navRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    }
    document.addEventListener("mousedown", handlePointer);
    return () => document.removeEventListener("mousedown", handlePointer);
  }, [menuOpen]);

  // Lock background scroll while the dropdown is open, so the page behind the
  // menu doesn't scroll under it (the menu only opens at hamburger widths).
  useEffect(() => {
    if (!menuOpen) return;
    document.body.classList.add("app-nav-menu-open");
    return () => document.body.classList.remove("app-nav-menu-open");
  }, [menuOpen]);

  // Close on Escape
  useEffect(() => {
    if (!menuOpen) return;
    function handleKey(e: KeyboardEvent) {
      if (e.key === "Escape") setMenuOpen(false);
    }
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [menuOpen]);

  function handleNavigate(section: ShellSection) {
    // Close the menu in the SAME transition as the route change. navigate() is a
    // (deferred) transition, so an urgent setMenuOpen(false) would unmount the
    // backdrop one frame BEFORE the new page renders - flashing the bright lobby.
    // Wrapping both makes them commit together, so the dim stays until the swap.
    startTransition(() => {
      setMenuOpen(false);
      setPlayersOpen(false);
      onNavigate(section);
    });
  }

  return (
    <header className="app-nav" ref={navRef}>
      <div className="app-nav-inner">
        <button className="app-logo" onClick={() => handleNavigate("lobby")} title="Achtung Royale home">
          <img className="app-logo-icon" src={asset("/logo-mark.svg")} alt="" />
          <span className="app-logo-titlewrap">
            <span className="app-logo-text">ACHTUNG<span>·</span>ROYALE</span>
            <span className="app-logo-version" aria-hidden="true">v{GAME_VERSION}</span>
          </span>
        </button>

        <nav className="app-nav-links" aria-label="Main navigation">
          {links.map((l) => (
            <button
              key={l.id}
              className={"app-nav-link" + (l.secondary ? " app-nav-link--secondary" : "") + (l.tight ? " app-nav-link--tight" : "") + (l.id === "shop" ? " app-nav-link--shop" : "") + (active === l.id ? " active" : "")}
              onClick={() => handleNavigate(l.id)}
            >
              {l.label}
              {/* achievements waiting to be opened (their eggs): a gold count */}
              {l.id === "profile" && unopened.length > 0 && <span className="app-nav-count" aria-label={`${unopened.length} to open`}>{unopened.length}</span>}
              {/* won items not looked at yet (the Royale's crown) */}
              {l.id === "shop" && newItems.length > 0 && <span className="app-nav-count" aria-label={`${newItems.length} new`}>{newItems.length}</span>}
            </button>
          ))}
        </nav>


        <div className="app-nav-sep" aria-hidden="true" />

        <div className="app-nav-right">
          <div className="app-nav-icons">
            {icons.map((l) => (
              <button
                key={l.id}
                type="button"
                className={"app-nav-icon" + (active === l.id ? " active" : "")}
                onClick={() => handleNavigate(l.id)}
                aria-label={l.label}
                title={l.label}
                aria-current={active === l.id ? "page" : undefined}
              >
                {l.icon}
                {l.id === "news" && newsUnread && <i className="app-nav-dot" aria-label="New" />}
              </button>
            ))}
            {/* ⛶: fullscreen for the visit (shared/fullscreen.ts). Absent where a
                page cannot go fullscreen (iPhone). */}
            <FullscreenButton className="app-nav-fullscreen" />
          </div>
          {/* The profile chip, one button for you and your account: signed out
              it says Sign in under your name. While the session check is still
              loading we render optimistically from the last-known state
              (signedInHint), so the line never blinks in or out. */}
          <div className="app-nav-auth">
            {showSignIn ? (
              // Signed out: Sign in on the bar, opening the same player menu
              // (name, keys, what you wear) with a full-width Sign in at its foot.
              <button type="button" className={"app-nav-signin" + (playersOpen ? " open" : "")} onClick={() => setPlayersOpen((v) => !v)} aria-expanded={playersOpen}>Sign in</button>
            ) : (
              <button
                className={"app-nav-profile" + (playersOpen ? " open" : "")}
                onClick={() => setPlayersOpen((v) => !v)}
                aria-label="Your player"
                aria-expanded={playersOpen}
                title={me.name}
              >
                <img className="app-nav-profile-flag" src={flagUrl(me.flag)} alt="" />
                {/* just the name: the ranks are on the Ranked cards */}
                <span className="app-nav-profile-text"><b>{me.name || "Guest"}</b></span>
              </button>
            )}
            {playersOpen && <PlayerMenu roster={roster} onClose={() => setPlayersOpen(false)} onLogin={onLogin} onAccount={onAccount} />}
          </div>

          {/* Hamburger — hidden on desktop via CSS; shown at ≤ 1024px */}
          <button
            className={"app-nav-hamburger" + (menuOpen ? " open" : "")}
            onClick={() => setMenuOpen((v) => !v)}
            aria-label={menuOpen ? "Close menu" : "Open navigation menu"}
            aria-expanded={menuOpen}
            aria-controls="app-nav-mobile-menu"
          >
            <span aria-hidden="true" />
            <span aria-hidden="true" />
            <span aria-hidden="true" />
          </button>
        </div>
      </div>

      {/* Backdrop — covers the page while the menu is open so a tap outside the
          menu just closes it instead of activating whatever is behind it. */}
      <div className={"app-nav-backdrop" + (menuOpen ? " open" : "")} aria-hidden="true" onClick={() => setMenuOpen(false)} />

      {/* The menu: a dropdown under the bar at tablet widths, a drawer sliding
          in from the left on phones. Always mounted so it can animate both
          ways (CSS shows it via .open); inert while closed so nothing in it
          can be focused or read. */}
      {(
        <div className={"app-nav-mobile-menu" + (menuOpen ? " open" : "")} id="app-nav-mobile-menu" {...({ inert: menuOpen ? undefined : "" } as Record<string, unknown>)}>
          <nav aria-label="Mobile navigation">
            {[...links, ...icons].map((l) => (
              <React.Fragment key={l.id}>
                <button
                  className={"app-nav-mobile-link" + (l.id === "shop" ? " app-nav-mobile-link--shop" : "") + (active === l.id ? " active" : "")}
                  onClick={() => handleNavigate(l.id)}
                >
                  {l.label}
                </button>
              </React.Fragment>
            ))}
          </nav>
          <div className="app-nav-mobile-account">
            {user ? (
              <button className="app-nav-mobile-link app-nav-mobile-account-btn" onClick={() => { setMenuOpen(false); onAccount(); }}>
                {user.user_metadata?.avatar_url ? (
                  <img className="app-nav-avatar" src={user.user_metadata.avatar_url} alt="" />
                ) : (
                  <span className="app-nav-avatar app-nav-avatar--fallback" aria-hidden="true">
                    {resolveDisplayName(user).charAt(0).toUpperCase()}
                  </span>
                )}
                <span>{resolveDisplayName(user)}</span>
              </button>
            ) : (
              <button className="app-nav-mobile-link" onClick={() => { setMenuOpen(false); onLogin(); }}>
                Sign in
              </button>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
